import React, { useState } from 'react';
import { InterviewerCard } from '../../types/interviewer';
import { TimeSlot } from '../../types/booking';
import { StaticTermsModal } from '../legal/StaticTermsModal';
import { getFullImageUrl } from '../../utils/imageUtils';
import { CurrencyDisplay, CurrencySelect } from '../CurrencyDisplay';
import { DEFAULT_AVATAR } from '../../utils/defaultAvatar';

export type BookingSummaryInterviewer = InterviewerCard & { fullName?: string };

interface BookingSummaryProps {
  interviewer: BookingSummaryInterviewer;
  selectedSlot: TimeSlot;
  onConfirmBooking: (selectedCurrency?: 'USD' | 'GEL', selectedAmount?: number) => void;
  onCancel: () => void;
  loading?: boolean;
}

export function BookingSummary({ 
  interviewer, 
  selectedSlot, 
  onConfirmBooking, 
  onCancel, 
  loading = false 
}: BookingSummaryProps) {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [cancellationAccepted, setCancellationAccepted] = useState(false);
  const [recordingConsent, setRecordingConsent] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  
  // Currency selection state
  const [selectedCurrency, setSelectedCurrency] = useState<'USD' | 'GEL'>(selectedSlot.currency as 'USD' | 'GEL');
  const [selectedAmount, setSelectedAmount] = useState<number>(selectedSlot.amount || 0);

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return {
      date: date.toLocaleDateString('en-GB', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: '2-digit' 
      }),
      time: date.toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      })
    };
  };

  const dateTime = formatDateTime(selectedSlot.startTime);
  const endTime = new Date(selectedSlot.endTime).toLocaleTimeString('en-GB', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });

  const duration = Math.round((new Date(selectedSlot.endTime).getTime() - new Date(selectedSlot.startTime).getTime()) / (1000 * 60));

  const canProceed = termsAccepted && privacyAccepted && cancellationAccepted && recordingConsent;
  const displayName = interviewer.fullName ?? interviewer.profileName;
  const ratingStars = Math.min(5, Math.max(0, Math.floor(interviewer.rating)));

  return (
    <div className="mx-auto max-w-2xl overflow-hidden rounded-stitch-2xl border border-stitch-border bg-stitch-surface shadow-stitch-pop">
      {/* Header */}
      <div className="bg-gradient-to-r from-stitch-primary to-stitch-accent px-6 py-4">
        <h2 className="text-2xl font-bold text-white">Booking Summary</h2>
        <p className="text-stitch-surface/90">Please review your interview details</p>
      </div>

      <div className="p-6">
        {/* Interviewer Info */}
        <div className="mb-6 rounded-stitch-xl border border-stitch-border bg-stitch-surface-2 p-6">
          <div className="flex flex-col items-center space-y-4 sm:flex-row sm:items-start sm:space-x-6 sm:space-y-0">
            <div className="flex-shrink-0">
              {getFullImageUrl(interviewer.profilePictureUrl) ? (
                <img 
                  src={getFullImageUrl(interviewer.profilePictureUrl) || DEFAULT_AVATAR} 
                  alt={displayName}
                  className="h-24 w-24 rounded-full border-2 border-white object-cover shadow-stitch-pop"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                  }}
                />
              ) : (
                <div className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-stitch-surface bg-gradient-to-br from-stitch-primary to-stitch-accent shadow-stitch-pop">
                  <span className="material-symbols-outlined text-5xl text-white">person</span>
                </div>
              )}
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="mb-1 text-xl font-bold text-stitch-fg">{displayName}</h3>
              
              {/* Professional Title & Company */}
              <div className="mb-2">
                <p className="text-lg font-semibold text-stitch-primary">{interviewer.specialty}</p>
                {interviewer.company && (
                  <p className="text-stitch-fg-soft font-medium">at {interviewer.company}</p>
                )}
              </div>

              {/* Experience */}
              {interviewer.experience && (
                <div className="mb-2">
                  <span className="inline-flex items-center rounded-full bg-stitch-success-soft px-2 py-1 text-xs font-medium text-stitch-success dark:text-stitch-success">
                    {interviewer.experience} Experience
                  </span>
                </div>
              )}

              {/* Rating */}
              <div className="mb-2 flex items-center justify-center sm:justify-start">
                <div className="flex items-center rounded-full bg-stitch-warning-soft/60 px-2 py-1 dark:bg-stitch-warning-soft/20">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span
                      key={i}
                      className={`material-symbols-outlined text-lg ${
                        i < ratingStars ? 'text-stitch-warning' : 'text-stitch-border'
                      }`}
                      style={{ fontVariationSettings: i < ratingStars ? "'FILL' 1, 'wght' 500" : "'wght' 400" }}
                    >
                      star
                    </span>
                  ))}
                  <span className="ml-1 text-xs font-semibold text-stitch-fg-soft">
                    {interviewer.rating.toFixed(1)} ({interviewer.totalInterviews || 0} interviews)
                  </span>
                </div>
              </div>

              {/* Languages */}
              {interviewer.languages && interviewer.languages.length > 0 && (
                <div className="mb-2">
                  <div className="flex flex-wrap justify-center sm:justify-start gap-1">
                    {interviewer.languages.map((lang, index) => (
                      <span 
                        key={index} 
                        className="inline-block rounded-full bg-stitch-primary-soft px-2 py-1 text-xs font-medium text-stitch-primary"
                      >
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Location */}
              {(interviewer.city || interviewer.country) && (
                <div className="mb-2">
                  <div className="flex items-center justify-center sm:justify-start text-xs text-stitch-muted">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {[interviewer.city, interviewer.country].filter(Boolean).join(', ')}
                  </div>
                </div>
              )}

              {/* LinkedIn Profile */}
              {interviewer.linkedinUrl && (
                <div className="mt-3">
                  <a
                    href={interviewer.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-stitch-lg bg-stitch-primary px-3 py-1 text-sm font-medium text-white transition-colors hover:bg-stitch-primary-hover"
                  >
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                    </svg>
                    LinkedIn
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Interview Details */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between rounded-stitch-xl border border-stitch-border p-4">
            <div className="flex items-center space-x-3">
              <span className="material-symbols-outlined text-stitch-muted">schedule</span>
              <div>
                <p className="text-sm font-medium text-stitch-fg">Interview Date & Time</p>
                <p className="text-sm text-stitch-muted">{dateTime.date}</p>
                <p className="text-sm text-stitch-muted">{dateTime.time} - {endTime}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-stitch-muted">Duration</p>
              <p className="text-sm font-medium text-stitch-fg">{duration} minutes</p>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-stitch-xl border border-stitch-border p-4">
            <div className="flex items-center space-x-3">
              <span className="material-symbols-outlined text-stitch-muted">payments</span>
              <div>
                <p className="text-sm font-medium text-stitch-fg">Interview Fee</p>
                <p className="text-sm text-stitch-muted">One-time payment</p>
              </div>
            </div>
            <div className="text-right">
              {selectedSlot.currency === 'EUR' ? (
                <div className="text-2xl font-bold text-stitch-success">
                  €{selectedSlot.amount?.toFixed(2) || '0.00'} EUR
                </div>
              ) : (
                <CurrencyDisplay 
                  amount={selectedSlot.amount || 0}
                  currency={selectedSlot.currency as 'USD' | 'GEL'}
                  showConversionInfo={true}
                  className="text-2xl font-bold text-stitch-success"
                />
              )}
            </div>
          </div>
        </div>

        {/* Currency Selection for Georgian Users - Only show for USD/GEL, not EUR */}
        {selectedSlot.currency !== 'EUR' && (
          <CurrencySelect
            amount={selectedSlot.amount || 0}
            currency={selectedSlot.currency as 'USD' | 'GEL'}
            selectedCurrency={selectedCurrency}
            onCurrencySelect={(currency, amount) => {
              setSelectedCurrency(currency);
              setSelectedAmount(amount);
            }}
          />
        )}

        {/* Terms and Conditions */}
        <div className="mb-6 rounded-stitch-lg border border-stitch-warning/30 bg-stitch-warning-soft/40 p-4 dark:bg-stitch-warning-soft/10">
          <h4 className="text-lg font-semibold text-stitch-fg mb-3">Terms and Conditions</h4>
          <p className="text-sm text-stitch-muted mb-4">Please review and accept the following to continue:</p>
          
          <div className="space-y-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-stitch-border text-stitch-primary focus:ring-stitch-ring"
              />
              <div className="text-sm text-stitch-fg-soft">
                <span>I have read and agree to the </span>
                <button 
                  onClick={() => setShowTermsModal(true)}
                  className="font-medium text-stitch-primary underline hover:text-stitch-primary-hover"
                  type="button"
                >
                  Terms of Service
                </button>
              </div>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-stitch-border text-stitch-primary focus:ring-stitch-ring"
              />
              <div className="text-sm text-stitch-fg-soft">
                <span>I have read and agree to the </span>
                <button 
                  onClick={() => setShowPrivacyModal(true)}
                  className="font-medium text-stitch-primary underline hover:text-stitch-primary-hover"
                  type="button"
                >
                  Privacy Policy
                </button>
              </div>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cancellationAccepted}
                onChange={(e) => setCancellationAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-stitch-border text-stitch-primary focus:ring-stitch-ring"
              />
              <div className="text-sm text-stitch-fg-soft">
                <span>I understand and agree to the </span>
                <button 
                  onClick={() => setShowCancellationModal(true)}
                  className="font-medium text-stitch-primary underline hover:text-stitch-primary-hover"
                  type="button"
                >
                  Cancellation and Refund Policy
                </button>
              </div>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={recordingConsent}
                onChange={(e) => setRecordingConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-stitch-border text-stitch-primary focus:ring-stitch-ring"
              />
              <div className="text-sm text-stitch-fg-soft">
                <span>I consent to this interview session being </span>
                <span className="font-medium text-stitch-danger">recorded</span>
                <span> for quality assurance and review purposes. Recordings will be securely stored and accessible through the platform.</span>
              </div>
            </label>
          </div>
        </div>

        {/* Important Notes */}
        <div className="mb-6 rounded-stitch-lg border border-stitch-border bg-stitch-info-soft/50 p-4 dark:bg-stitch-info-soft/15">
          <h4 className="mb-2 text-sm font-semibold text-stitch-fg">Important Notes:</h4>
          <ul className="space-y-1 text-xs text-stitch-fg-soft">
            <li>• You will receive a confirmation email with interview details</li>
            <li>• A Google Meet link will be sent via email to both parties</li>
            <li>• Please test your camera and microphone before the interview</li>
            <li>
              • <span className="font-medium">The session will be automatically recorded</span> and available after
              completion
            </li>
            <li>• Cancellations must be made at least 24 hours in advance for full refund</li>
          </ul>
        </div>

        {/* Payment Methods */}
        <div className="mb-6 rounded-stitch-xl border border-stitch-border bg-stitch-surface p-4">
          <h4 className="text-sm font-semibold text-stitch-fg mb-3">Secure Payment Methods</h4>
          <div className="flex items-center justify-center space-x-4">
            {/* Payment Methods Image */}
            <div className="flex items-center justify-center rounded-stitch-lg border border-stitch-border bg-stitch-surface px-6 py-4 shadow-stitch-card">
              <img 
                src="/mastercard-visa-cards-logos-icons-701751695036083sdqsk5ncvn.png" 
                alt="VISA and MasterCard accepted" 
                className="h-16 object-contain"
                onError={(e) => {
                  // Fallback if image not found
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
            
            {/* Security Badge */}
            <div className="flex items-center text-xs text-stitch-muted ml-4">
              <svg className="mr-1 h-4 w-4 text-stitch-success" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>SSL Secured</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
          <button
            onClick={onCancel}
            className="flex-1 rounded-stitch-lg border border-stitch-border px-6 py-3 font-medium text-stitch-fg-soft transition-colors hover:bg-stitch-surface-2 focus:outline-none focus:ring-2 focus:ring-stitch-ring focus:ring-offset-2 focus:ring-offset-stitch-canvas"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirmBooking(selectedCurrency, selectedAmount)}
            disabled={!canProceed || loading}
            className={`flex-1 rounded-stitch-lg px-6 py-3 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              canProceed && !loading
                ? 'bg-stitch-primary text-white hover:bg-stitch-primary-hover focus:ring-stitch-ring'
                : 'cursor-not-allowed bg-stitch-muted text-stitch-fg'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span className="ml-2">Processing...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <span className="material-symbols-outlined mr-2 text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                  check_circle
                </span>
                Proceed to Payment
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Terms Modals */}
      <StaticTermsModal
        type="terms"
        title="Terms of Service"
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
      
      <StaticTermsModal
        type="privacy"
        title="Privacy Policy"
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
      
      <StaticTermsModal
        type="cancellation"
        title="Cancellation and Refund Policy"
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
      />
    </div>
  );
}
